// @flow
import _ from 'lodash';
import { KlaviyoAction } from 'stores/flow-type/klaviyo.type';
import {
  KLAVIYO_STATUS,
  GET_KLAVIYO_STATUS_REQUEST,
  GET_KLAVIYO_STATUS_SUCCESS,
  GET_KLAVIYO_STATUS_FAILURE,
  TOGGLE_KLAVIYO_REQUEST,
  TOGGLE_KLAVIYO_SUCCESS,
  TOGGLE_KLAVIYO_FAILURE,
  UPDATE_KLAVIYO_BANNER_REQUEST,
  UPDATE_KLAVIYO_BANNER_SUCCESS,
  UPDATE_KLAVIYO_BANNER_FAILURE,
  PREVIEW_KLAVIYO_RECOMMENDATION_REQUEST,
  PREVIEW_KLAVIYO_RECOMMENDATION_SUCCESS,
  PREVIEW_KLAVIYO_RECOMMENDATION_FAILURE,
  GET_KLAVIYO_RECOMMENDATIONS_REQUEST,
  GET_KLAVIYO_RECOMMENDATIONS_SUCCESS,
  GET_KLAVIYO_RECOMMENDATIONS_FAILURE,
  GET_KLAVIYO_RECOMMENDATION_REQUEST,
  GET_KLAVIYO_RECOMMENDATION_SUCCESS,
  GET_KLAVIYO_RECOMMENDATION_FAILURE,
  CREATE_KLAVIYO_RECOMMENDATION_REQUEST,
  CREATE_KLAVIYO_RECOMMENDATION_SUCCESS,
  CREATE_KLAVIYO_RECOMMENDATION_FAILURE,
  UPDATE_KLAVIYO_RECOMMENDATION_REQUEST,
  UPDATE_KLAVIYO_RECOMMENDATION_SUCCESS,
  UPDATE_KLAVIYO_RECOMMENDATION_FAILURE,
  DELETE_KLAVIYO_RECOMMENDATION_SUCCESS
} from '../flow-type/klaviyo.type';

type KlaviyoState = {
  status: string,
  getStatusLoading: boolean,
  getStatusSuccess: boolean,
  webFeeds: Array<any>,
  newWebFeeds: Array<any>,
  toggleStatusLoading: boolean,
  toggleStatusSuccess: boolean,
  showDashboardBanner: boolean,
  showKlaviyoBanner: boolean,
  previewRecommendationLoading: boolean,
  previewRecommendationProducts: Array<any>,
  previewRecommendationTotal: number,
  previewRecommendationError: boolean,
  recommendations: Array<any>,
  recommendationsLoading: boolean,
  currentRecommendation: ?Object,
  currentRecommendationLoading: boolean,
  savingRecommendation: boolean,
};

const initialState: KlaviyoState = {
  status: KLAVIYO_STATUS.INACTIVE,
  getStatusLoading: false,
  getStatusSuccess: false,
  webFeeds: [],
  newWebFeeds: [],

  toggleStatusLoading: false,
  toggleStatusSuccess: false,
  showDashboardBanner: false,
  showKlaviyoBanner: false,

  previewRecommendationLoading: false,
  previewRecommendationProducts: [],
  previewRecommendationTotal: 0,
  previewRecommendationError: false,

  recommendations: [],
  recommendationsLoading: false,
  currentRecommendation: null,
  currentRecommendationLoading: false,
  savingRecommendation: false,
};

// Keep the list in sync without a full refetch: replace the row if it already exists (update),
// otherwise append it (create).
const upsertRecommendation = (recommendations, recommendation) => {
  if (!recommendation) return recommendations;
  const exists = recommendations.some(r => r.id === recommendation.id);
  if (!exists) return [...recommendations, recommendation];
  return recommendations.map(r => (r.id === recommendation.id ? recommendation : r));
};

const klaviyoReducers = (state: KlaviyoState = initialState, action: KlaviyoAction) => {
  const { type } = action;

  switch (type) {
    case GET_KLAVIYO_STATUS_REQUEST:
      return {
        ...state,
        getStatusSuccess: false,
        getStatusLoading: true
      };
    case GET_KLAVIYO_STATUS_SUCCESS: {
      const { data } = action.payload;
      return {
        ...state,
        status: _.get(data, 'klaviyoOAuth.status'),
        webFeeds: _.get(data, 'klaviyoOAuth.webFeeds', []),
        newWebFeeds: _.get(data, 'klaviyoOAuth.newWebFeeds', []),
        showDashboardBanner: _.get(data, 'klaviyoOAuth.showDashboardBanner', false),
        showKlaviyoBanner: _.get(data, 'klaviyoOAuth.showKlaviyoBanner', false),
        getStatusLoading: false,
        getStatusSuccess: true,
        error: undefined
      };
    }
    case GET_KLAVIYO_STATUS_FAILURE:
      return {
        ...state,
        getStatusLoading: false,
        getStatusSuccess: false,
        error: action.payload
      };

    case TOGGLE_KLAVIYO_REQUEST: {
      const status = action.payload;
      return {
        ...state,
        status,
        toggleStatusSuccess: false,
        toggleStatusLoading: true
      };
    }
    case TOGGLE_KLAVIYO_SUCCESS: {
      const { data } = action.payload;
      return {
        ...state,
        status: _.get(data, 'data.klaviyoOAuth.status'),
        toggleStatusLoading: false,
        toggleStatusSuccess: true,
        error: undefined
      };
    }
    case TOGGLE_KLAVIYO_FAILURE:
      return {
        ...state,
        status: !state.status,
        toggleStatusLoading: false,
        toggleStatusSuccess: false,
        error: action.payload
      };

    case UPDATE_KLAVIYO_BANNER_REQUEST: {
      const { showDashboardBanner, showKlaviyoBanner } = action.payload;
      if (!_.isNil(showDashboardBanner)) {
        return {
          ...state,
          error: undefined,
          showDashboardBanner
        };
      }
      if (!_.isNil(showKlaviyoBanner)) {
        return {
          ...state,
          error: undefined,
          showKlaviyoBanner
        };
      }

      return state;
    }
    case UPDATE_KLAVIYO_BANNER_SUCCESS: {
      return {
        ...state,
        error: undefined
      };
    }
    case UPDATE_KLAVIYO_BANNER_FAILURE: {
      const { data, error } = action.payload;
      if (!_.isNil(data?.showDashboardBanner)) {
        return {
          ...state,
          error,
          showDashboardBanner: !data?.showDashboardBanner
        };
      }
      if (!_.isNil(data?.showKlaviyoBanner)) {
        return {
          ...state,
          error,
          showKlaviyoBanner: !data?.showKlaviyoBanner
        };
      }

      return state;
    }

    case PREVIEW_KLAVIYO_RECOMMENDATION_REQUEST:
      return {
        ...state,
        previewRecommendationLoading: true,
        previewRecommendationError: false
      };
    case PREVIEW_KLAVIYO_RECOMMENDATION_SUCCESS: {
      const { data } = action.payload;
      return {
        ...state,
        previewRecommendationLoading: false,
        previewRecommendationProducts: _.get(data, 'products', []),
        previewRecommendationTotal: _.get(data, 'total', 0),
        previewRecommendationError: false
      };
    }
    // Preview total 0 here means "we couldn't ask" (network/fe-api down), not "0 real matches" —
    // callers must not treat this the same as a genuine 0-result BRD 2.6 block (see builder.jsx
    // handleSave, which only blocks save on a *successful* 0, not a failed preview call).
    case PREVIEW_KLAVIYO_RECOMMENDATION_FAILURE:
      return {
        ...state,
        previewRecommendationLoading: false,
        previewRecommendationProducts: [],
        previewRecommendationTotal: 0,
        previewRecommendationError: true
      };

    case GET_KLAVIYO_RECOMMENDATIONS_REQUEST:
      return { ...state, recommendationsLoading: true };
    case GET_KLAVIYO_RECOMMENDATIONS_SUCCESS:
      return {
        ...state,
        recommendationsLoading: false,
        recommendations: _.get(action.payload, 'data.recommendations', [])
      };
    case GET_KLAVIYO_RECOMMENDATIONS_FAILURE:
      return { ...state, recommendationsLoading: false, recommendations: [] };

    case GET_KLAVIYO_RECOMMENDATION_REQUEST:
      return { ...state, currentRecommendationLoading: true, currentRecommendation: null };
    case GET_KLAVIYO_RECOMMENDATION_SUCCESS:
      return {
        ...state,
        currentRecommendationLoading: false,
        currentRecommendation: _.get(action.payload, 'data.recommendation', null)
      };
    case GET_KLAVIYO_RECOMMENDATION_FAILURE:
      return { ...state, currentRecommendationLoading: false, currentRecommendation: null };

    case CREATE_KLAVIYO_RECOMMENDATION_REQUEST:
    case UPDATE_KLAVIYO_RECOMMENDATION_REQUEST:
      return { ...state, savingRecommendation: true };
    case CREATE_KLAVIYO_RECOMMENDATION_SUCCESS:
    case UPDATE_KLAVIYO_RECOMMENDATION_SUCCESS: {
      const recommendation = _.get(action.payload, 'data.recommendation', null);
      return {
        ...state,
        savingRecommendation: false,
        currentRecommendation: recommendation,
        recommendations: upsertRecommendation(state.recommendations, recommendation)
      };
    }
    case CREATE_KLAVIYO_RECOMMENDATION_FAILURE:
    case UPDATE_KLAVIYO_RECOMMENDATION_FAILURE:
      return {
        ...state,
        savingRecommendation: false
      };

    case DELETE_KLAVIYO_RECOMMENDATION_SUCCESS: {
      const { recommendationId } = action.payload;
      return {
        ...state,
        recommendations: state.recommendations.filter(r => r.id !== recommendationId)
      };
    }

    default:
      return state;
  }
};

export default klaviyoReducers;
